<script lang="ts">
  import FormInputError from "@shared/svelte/Components/FormInputError.svelte";
  import TextInput from "@shared/svelte/Components/TextInput.svelte";
  import PhoneInput from "../PhoneInput.svelte";
  import EmailInput from "@shared/svelte/Components/EmailInput.svelte";
  import Footer from "./Footer.svelte";
  import Counter from "./Counter.svelte";
  import LiquorLicenseImageUpload from "../LiquorLicenseImageUpload.svelte";
  import apiutil from "@shared/svelte/Components/Autocomplete/api_util.js";
  import SimpleAutocompleteDropdown from "@shared/svelte/Components/Autocomplete/SimpleAutocompleteDropdown.svelte";
  import DatePicker from "@shared/svelte/Components/DatePicker.svelte";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import { get } from "svelte/store";
  import { form as svelteForm, field } from "svelte-forms2";
  import { email as emailValidator, pattern, required } from "svelte-forms2/validators";
  import * as _ from "lodash";
  import type { Distributor } from "@shared/types/Distributor";
  import type { AccountApplication } from "@shared/types/AccountApplication";
  import type { HTTPParams } from "@shared/types/HTTP";
  import type { FieldValidation, Validator } from "svelte-forms2";
  import type { Field } from "svelte-forms2/types";

  export let id: number = null;
  export let application: AccountApplication = <AccountApplication>{};
  export let exitForm: () => void;
  export let step: number = 1;
  export let formDisabled: boolean = false;
  export let goToNextStep: (id: number, nextStep: number) => void;

  const phoneRegExp = /^([2-9]\d{2}){2}\d{4}$/;
  const selectedDistributor = field("selected_distributor", application.distributor || null, [
    validateDistributor(),
  ]);
  const buyerName = field("buyer_contact_name", application.buyer_contact_name || "", [required()]);
  const email = field("email", application.email || "", [required(), emailValidator()]);
  const phone = field("phone", application.phone || "", [required(), pattern(phoneRegExp)]);
  const fax = field("fax", application.fax, [phoneValidator(false)]);
  const billingContact = field("billing_contact", application.billing_contact || "", [required()]);
  const billingContactEmail = field(
    "billing_contact_email",
    application.billing_contact_email || "",
    [required(), emailValidator()]
  );
  const billingContactPhone = field(
    "billing_contact_phone",
    application.billing_contact_phone || "",
    [required(), pattern(phoneRegExp)]
  );
  const billingAddress = field("billing_address", application.billing_address || "", [required()]);
  const deliveryAddress = field("delivery_address", application.delivery_address || "", [
    required(),
  ]);
  const deliveryInstructions = field(
    "delivery_instructions",
    application.delivery_instructions || "",
    [required()]
  );
  const companyType = field("company_type", application.company_type || "", [required()]);
  const employerIdentificationNumber = field(
    "employer_identification_number",
    application.employer_identification_number || "",
    [required()]
  );
  const fullCorporateName = field("full_corporate_name", application.full_corporate_name || "", [
    required(),
  ]);
  const liquorLicenseImageId = field(
    "liquor_license_image_id",
    application.liquor_license_image_id || null,
    [validateLiquorLicenseRequired()]
  );
  const liquorLicenseImageName = field(
    "liquor_license_image_name",
    application.liquor_license_image_name || null,
    [validateLiquorLicenseRequired()]
  );
  const liquorLicense = field("liquor_license", application.liquor_license || "", [
    validateLiquorLicenseRequired(),
  ]);
  const licenseExpirationDate = field(
    "license_expiration_date",
    application.license_expiration_date || "",
    [validateLiquorLicenseRequired()]
  );
  const pendingLiquorLicense = field(
    "pending_liquor_license",
    application.pending_liquor_license || false,
    []
  );
  const resaleCertNumber = field("resale_cert_number", application.resale_cert_number || "", [
    required(),
  ]);
  const tradeReferences = field("trade_references", application.trade_references || "", [
    nonCODStateRequiredValidator(),
  ]);
  const bankReferences = field("bank_references", application.bank_references || "", [
    nonCODStateRequiredValidator(),
  ]);
  const ownerName = field("owner_name", application.owner_name || "", [required()]);
  const ownerEmail = field("owner_email", application.owner_email || "", [
    required(),
    emailValidator(),
  ]);
  const ownerPhone = field("owner_phone", application.owner_phone || "", [
    required(),
    pattern(phoneRegExp),
  ]);
  const ownerHomeAddress = field("owner_home_address", application.owner_home_address || "", [
    required(),
  ]);
  const salesRepName = field("sales_rep_name", application.sales_rep_name || "", []);
  const applicationForm = svelteForm(
    bankReferences,
    billingAddress,
    billingContact,
    billingContactEmail,
    billingContactPhone,
    buyerName,
    companyType,
    deliveryAddress,
    deliveryInstructions,
    email,
    employerIdentificationNumber,
    fax,
    fullCorporateName,
    licenseExpirationDate,
    liquorLicense,
    liquorLicenseImageId,
    liquorLicenseImageName,
    ownerName,
    ownerEmail,
    ownerPhone,
    ownerHomeAddress,
    pendingLiquorLicense,
    phone,
    resaleCertNumber,
    salesRepName,
    selectedDistributor,
    tradeReferences
  );

  let overrideDistributorValidation: boolean = true;
  let disableSaveAndExit: boolean = false;

  function validateDistributor(): Validator {
    return (value: Distributor) => {
      const valid =
        overrideDistributorValidation || (required()(value?.id) as FieldValidation).valid;
      disableSaveAndExit = !valid;
      return {
        valid,
        name: "required",
      };
    };
  }

  function validateLiquorLicenseRequired(): Validator {
    return (value: string) => {
      return {
        valid: $pendingLiquorLicense.value || (required()(value) as FieldValidation).valid,
        name: "required",
      };
    };
  }

  const validatePendingLiquorLicenseAndSaveForm = () => {
    $pendingLiquorLicense.value = !$pendingLiquorLicense.value;
    liquorLicenseImageId.validate();
    liquorLicenseImageName.validate();
    liquorLicense.validate();
    licenseExpirationDate.validate();
    if ($pendingLiquorLicense.value) {
      licenseExpirationDate.set("");
    }
    saveForm();
  };

  const removeEmptyEntries = (obj: object) => {
    return _.fromPairs(
      Object.entries(obj)
        .filter(([_, store]) => {
          let field = get(store) as Field<any>;
          let value = field.value;

          if (field.dirty && field.valid) return true;
          else return value !== "" && value !== null && value !== undefined;
        })
        .map(([k, store]) => [k, (get(store) as Field<any>).value])
    );
  };

  const saveAndExitForm = async () => {
    overrideDistributorValidation = false;
    selectedDistributor.validate();
    overrideDistributorValidation = true;
    if (!!$selectedDistributor.value?.id) {
      exitForm();
    }
  };

  const saveForm = async () => {
    const datePickerIsDirty =
      $licenseExpirationDate.value &&
      $licenseExpirationDate.valid &&
      $licenseExpirationDate.value !== application.license_expiration_date;

    if (!$applicationForm.dirty && !datePickerIsDirty) return;
    if (!$selectedDistributor.value || !Object.entries($selectedDistributor.value).length) return;

    const data = {
      ...removeEmptyEntries({
        bank_references: bankReferences,
        billing_address: billingAddress,
        billing_contact: billingContact,
        billing_contact_email: billingContactEmail,
        billing_contact_phone: billingContactPhone,
        buyer_contact_name: buyerName,
        company_type: companyType,
        delivery_address: deliveryAddress,
        delivery_instructions: deliveryInstructions,
        email: email,
        employer_identification_number: employerIdentificationNumber,
        fax: fax,
        full_corporate_name: fullCorporateName,
        license_expiration_date: licenseExpirationDate,
        liquor_license: liquorLicense,
        owner_name: ownerName,
        owner_email: ownerEmail,
        owner_phone: ownerPhone,
        owner_home_address: ownerHomeAddress,
        pending_liquor_license: pendingLiquorLicense,
        phone: phone,
        resale_cert_number: resaleCertNumber,
        sales_rep_name: salesRepName,
        trade_references: tradeReferences,
      }),
      liquor_license_image_id: $liquorLicenseImageId.value,
      liquor_license_image_name: $liquorLicenseImageName.value,
      distributor_id: $selectedDistributor.value?.id,
    };

    if (datePickerIsDirty) {
      Object.assign(data, {
        license_expiration_date: new Date($licenseExpirationDate.value).toISOString(),
      });
    }

    let req: HTTPParams;
    if (id) {
      req = {
        method: "PUT",
        url: `/api/retailer/account_applications/${id}`,
        data: {
          account_application: data,
        },
      };
    } else {
      req = {
        method: "POST",
        url: "/api/retailer/account_applications",
        data: {
          account_application: data,
        },
      };
    }

    try {
      const res = await HttpPromise(req);
      if (!id && res.id) {
        id = res.id;
        window["stateService"].go(
          "layout.apply",
          { id: id, step: 1 },
          { location: true, reload: false, notify: false }
        );
      }

      application = res;
    } catch (e) {
      if (e.errors.details && e.errors.details.distributor_id) clearDistributor();
      return e.errors.messages.forEach((message: string) =>
        Flash.now({ type: "error", message, rawHtml: true })
      );
    }
  };

  function phoneValidator(required: boolean = true): Validator {
    return (value: string) => {
      return {
        name: "pattern",
        valid: (!required && !value) || phoneRegExp.test(value),
      };
    };
  }

  function nonCODStateRequiredValidator(): Validator {
    return (value: string) => {
      return {
        name: "required",
        valid: $StateSettingsStore.cod_enabled || !!value,
      };
    };
  }

  const selectDatepicker = (_e: CustomEvent) => {
    const selectedValue = $licenseExpirationDate.value;
    licenseExpirationDate.reset();
    $licenseExpirationDate.value = selectedValue;
    saveForm();
  };

  const getScope = () => {
    return apiutil.prepare({
      objectType: "distributor",
      apiParams: { with_sales_reps: false, all_retailers: false },
      searchUrlInput: "/api/retailer/application_enabled_distributors",
      listMode: false,
    });
  };

  const apiHandler = async input => {
    return apiutil.searchAPI(scope, input);
  };

  const clearDistributor = () => {
    $selectedDistributor.value = null;
  };

  let scope = getScope();

  $: {
    scope = getScope();
  }

  const submitForm = async () => {
    overrideDistributorValidation = false;
    await applicationForm.validate();
    overrideDistributorValidation = true;

    if (!$applicationForm.valid) {
      return;
    } else {
      try {
        await HttpPromise({
          method: "PUT",
          url: `/api/retailer/account_applications/${id}`,
          data: {
            account_application: {
              status: "awaiting_signature",
            },
          },
        });
      } catch (e) {
        return e.errors.forEach((message: string) =>
          Flash.now({ type: "error", message, rawHtml: true })
        );
      }
      goToNextStep(id, 2);
    }
  };

</script>

<style>
  .center-datepicker {
    min-height: 90px;
  }

</style>

<main class="fizz-surface">
  <section class="fizz-container">
    <Counter {step} />
    <form
      on:submit|preventDefault={saveForm}
      class="fizz-container fizz-stack-16"
      class:fizz-inset-squish-96={!$LayoutStore.isMobile}
      class:fizz-inset-horizontal-16={$LayoutStore.isMobile}
      aria-label="distributor-account-form"
    >
      <fieldset
        class="fizz-inset-vertical-24 fizz-border-bottom fizz-align-content-start"
        class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
        class:fizz-flex-row-responsive={$LayoutStore.isMobile}
        disabled={formDisabled || null}
      >
        <div class="fizz-flex-item">
          <h3 class="fizz-heading-3 fizz-stack-8">Select a distributor</h3>
          <p class="fizz-text-medium fizz-stack-16 fizz-text-subdued">
            This should be a distributor you don't have an existing account with.
          </p>
        </div>

        <div class="fizz-stack-16 fizz-flex-item">
          <label
            class="fizz-label fizz-stack-8"
            class:fizz-full-width={$LayoutStore.isMobile}
            for="distributors_autocomplete"
          >
            <strong>Distributor</strong>
          </label>
          <SimpleAutocompleteDropdown
            {apiHandler}
            inputId="distributors_autocomplete"
            placeholder="Search distributors"
            field={selectedDistributor}
            showInputWhenDisabled={true}
            showCloseIcon={false}
            popoverFullWidth={true}
            on:clear={clearDistributor}
            bind:value={$selectedDistributor.value}
            on:change={saveForm}
          >
            <div slot="no_results" class="fizz-text-center">
              <p class="fizz-text-medium fizz-text-strong">No search results</p>
              <p class="fizz-text-medium fizz-text-regular">
                This distributor may not accept applications at this time
              </p>
            </div>
          </SimpleAutocompleteDropdown>
          <FormInputError form={applicationForm} errorName="selected_distributor.required">
            Distributor is required
          </FormInputError>
        </div>
      </fieldset>

      <div
        class="fizz-inset-vertical-24 fizz-border-bottom fizz-align-content-start"
        class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
        class:fizz-flex-row-responsive={$LayoutStore.isMobile}
      >
        <div class="fizz-flex-item">
          <h3 class="fizz-heading-3 fizz-stack-8">Buyer and owner details</h3>
          <p class="fizz-text-medium fizz-stack-16 fizz-text-subdued">
            Enter the contact information for the buyer and owner of your business.
          </p>
        </div>
        <div class="fizz-flex-item" class:fizz-full-width={$LayoutStore.isMobile}>
          <fieldset class="fizz-stack-16" disabled={formDisabled || null}>
            <legend class="fizz-stack-24">
              <h4 class="fizz-heading-4">Buyer details</h4>
            </legend>
            <TextInput
              name="buyer_contact_name"
              label="First and last name"
              field={buyerName}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="buyer_contact_name.required">
                First and last names are required
              </FormInputError>
            </TextInput>
            <EmailInput name="email" label="Email address" field={email} onblur={saveForm}>
              <FormInputError form={applicationForm} errorName="email.required">
                Email address is required
              </FormInputError>
              {#if !$applicationForm.hasError("email.required")}
                <FormInputError form={applicationForm} errorName="email.not_an_email">
                  Email address is invalid
                </FormInputError>
              {/if}
            </EmailInput>
            <fieldset
              class="fizz-flex-row-responsive fizz-stack-16 fizz-align-content-start"
              disabled={formDisabled || null}
            >
              <PhoneInput
                name="phone"
                label="Business phone"
                field={phone}
                onblur={saveForm}
                stack={false}
              >
                <FormInputError form={applicationForm} errorName="phone.required">
                  Business phone number is required
                </FormInputError>
                {#if !$applicationForm.hasError("phone.required")}
                  <FormInputError form={applicationForm} errorName="phone.pattern">
                    Business phone number is invalid
                  </FormInputError>
                {/if}
              </PhoneInput>
              <PhoneInput
                name="fax"
                label="Fax (optional)"
                field={fax}
                onblur={saveForm}
                stack={false}
              >
                {#if $fax.value}
                  <FormInputError form={applicationForm} errorName="fax.pattern">
                    Fax is invalid
                  </FormInputError>
                {/if}
              </PhoneInput>
            </fieldset>
          </fieldset>

          <fieldset disabled={formDisabled || null}>
            <legend
              class="fizz-stack-24 fizz-flex-row-responsive fizz-space-between fizz-full-width"
            >
              <h4 class="fizz-heading-4">Owner details</h4>
            </legend>
            <TextInput
              name="owner_name"
              label="First and last name"
              field={ownerName}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="owner_name.required">
                First and last name are required
              </FormInputError>
            </TextInput>
            <EmailInput
              name="owner_email"
              label="Email address"
              field={ownerEmail}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="owner_email.required">
                Email address is required
              </FormInputError>
              {#if !$applicationForm.hasError("owner_email.required")}
                <FormInputError form={applicationForm} errorName="owner_email.not_an_email">
                  Email address is invalid
                </FormInputError>
              {/if}
            </EmailInput>
            <TextInput
              name="owner_home_address"
              label="Home address"
              field={ownerHomeAddress}
              onblur={saveForm}
            >
              <p class="fizz-text-subdued">Street address (apt, suite, etc.), city, state, zip</p>
              <FormInputError form={applicationForm} errorName="owner_home_address.required">
                Home address is required
              </FormInputError>
            </TextInput>
            <PhoneInput
              name="owner_phone"
              label="Phone number"
              field={ownerPhone}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="owner_phone.required">
                Phone number is required
              </FormInputError>
              {#if !$applicationForm.hasError("owner_phone.required")}
                <FormInputError form={applicationForm} errorName="owner_phone.pattern">
                  Phone number is invalid
                </FormInputError>
              {/if}
            </PhoneInput>
          </fieldset>
        </div>
      </div>

      <div
        class="fizz-inset-vertical-24 fizz-border-bottom fizz-align-content-start"
        class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
        class:fizz-flex-row-responsive={$LayoutStore.isMobile}
      >
        <div class="fizz-flex-item">
          <h3 class="fizz-heading-3 fizz-stack-8">Billing and delivery address</h3>
          <p class="fizz-text-medium fizz-stack-16 fizz-text-subdued">
            Enter the billing and delivery address for your business. Additionally, add your
            preferred delivery day and time.
          </p>
        </div>

        <div class="fizz-flex-item" class:fizz-full-width={$LayoutStore.isMobile}>
          <fieldset class="fizz-stack-16" disabled={formDisabled || null}>
            <legend class="fizz-stack-24">
              <h4 class="fizz-heading-4">Billing address</h4>
            </legend>
            <TextInput
              name="billing_contact"
              label="First and last name"
              field={billingContact}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="billing_contact.required">
                First and last name are required
              </FormInputError>
            </TextInput>
            <EmailInput
              name="billing_contact_email"
              label="Email address"
              field={billingContactEmail}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="billing_contact_email.required">
                Email address is required
              </FormInputError>
              {#if !$applicationForm.hasError("billing_contact_email.required")}
                <FormInputError
                  form={applicationForm}
                  errorName="billing_contact_email.not_an_email"
                >
                  Email address is invalid
                </FormInputError>
              {/if}
            </EmailInput>
            <PhoneInput
              name="billing_contact_phone"
              label="Phone number"
              field={billingContactPhone}
              onblur={saveForm}
            >
              <FormInputError form={applicationForm} errorName="billing_contact_phone.required">
                Phone number is required
              </FormInputError>
              {#if !$applicationForm.hasError("billing_contact_phone.required")}
                <FormInputError form={applicationForm} errorName="billing_contact_phone.pattern">
                  Phone number is invalid
                </FormInputError>
              {/if}
            </PhoneInput>
            <TextInput
              name="billing_address"
              label="Billing address"
              field={billingAddress}
              onblur={saveForm}
            >
              <p class="fizz-text-subdued">Street address (apt, suite, etc.), city, state, zip</p>
              <FormInputError form={applicationForm} errorName="billing_address.required">
                Billing address is required
              </FormInputError>
            </TextInput>
          </fieldset>

          <fieldset disabled={formDisabled || null}>
            <legend
              class="fizz-stack-24 fizz-flex-row-responsive fizz-space-between fizz-full-width"
            >
              <h4 class="fizz-heading-4">Delivery details</h4>
            </legend>
            <TextInput
              name="delivery_address"
              label="Delivery address"
              field={deliveryAddress}
              onblur={saveForm}
            >
              <p class="fizz-text-subdued">Street address (apt, suite, etc.), city, state, zip</p>
              <FormInputError form={applicationForm} errorName="delivery_address.required">
                Delivery address is required
              </FormInputError>
            </TextInput>
            <TextInput
              name="delivery_instructions"
              label="Request a delivery day and time window"
              field={deliveryInstructions}
              onblur={saveForm}
            >
              <p class="fizz-text-subdued">
                Enter the delivery day and 4 hour window that you want to receive orders (e.g.,
                Mondays 12pm-4pm). This request will be accommodated as well as possible.
              </p>
              <FormInputError form={applicationForm} errorName="delivery_instructions.required">
                Delivery window is required
              </FormInputError>
            </TextInput>
          </fieldset>
        </div>
      </div>

      <div
        class="fizz-inset-vertical-24 fizz-border-bottom fizz-align-content-start"
        class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
        class:fizz-flex-row-responsive={$LayoutStore.isMobile}
      >
        <div class="fizz-flex-item">
          <h3 class="fizz-heading-3 fizz-stack-8">Business details</h3>
          <p class="fizz-stack-16 fizz-text-subdued">
            Enter your business information and liquor license.
          </p>
        </div>

        <fieldset
          class="fizz-flex-item"
          class:fizz-full-width={$LayoutStore.isMobile}
          disabled={formDisabled || null}
        >
          <label class="fizz-full-width fizz-inline-block fizz-stack-16">
            <p class="fizz-stack-8 fizz-label">
              <strong>Company type</strong>
            </p>
            <select
              name="company_type"
              class="fizz-select fizz-full-width"
              class:fizz-input-error={!$companyType.valid}
              bind:value={$companyType.value}
              on:blur={saveForm}
            >
              <option value="" disabled selected>Select Option</option>
              <option value="individual">Individual/Sole Proprietor</option>
              <option value="corporation">Corporation</option>
              <option value="partnership">Partnership</option>
              <option value="llc">LLC</option>
            </select>
            <FormInputError form={applicationForm} errorName="company_type.required">
              Company type is required
            </FormInputError>
          </label>
          <TextInput
            name="full_corporate_name"
            label="Full corporate name"
            field={fullCorporateName}
            onblur={saveForm}
          >
            <p class="fizz-text-subdued">As it appears on the business liquor license.</p>
            <FormInputError form={applicationForm} errorName="full_corporate_name.required">
              Full corporate name is required
            </FormInputError>
          </TextInput>
          <TextInput
            name="employer_identification_number"
            label="Employer identification number (EIN)"
            field={employerIdentificationNumber}
            onblur={saveForm}
          >
            <FormInputError
              form={applicationForm}
              errorName="employer_identification_number.required"
            >
              Employer identification number is required
            </FormInputError>
          </TextInput>
          <TextInput
            name="resale_cert_number"
            label="Resale certification number (Tax ID)"
            field={resaleCertNumber}
            onblur={saveForm}
          >
            <FormInputError form={applicationForm} errorName="resale_cert_number.required">
              Resale certification number is required
            </FormInputError>
          </TextInput>
          <LiquorLicenseImageUpload
            imageIdField={liquorLicenseImageId}
            imageNameField={liquorLicenseImageName}
            onChange={saveForm}
          >
            <FormInputError form={applicationForm} errorName="liquor_license_image_id.required">
              License image is required
            </FormInputError>
          </LiquorLicenseImageUpload>
          <TextInput
            name="liquor_license"
            label="License number"
            field={liquorLicense}
            onblur={saveForm}
          >
            <FormInputError form={applicationForm} errorName="liquor_license.required">
              License number is required
            </FormInputError>
          </TextInput>
          <fieldset
            class="fizz-flex-row-responsive fizz-space-between"
            disabled={formDisabled || null}
          >
            <label
              class="fizz-full-width fizz-flow-4 fizz-label fizz-stack-16"
              class:center-datepicker={!$LayoutStore.isMobile}
              for="license_expiration_date"
            >
              <p class="fizz-stack-8 fizz-label">
                <strong>License expiration date</strong>
              </p>
              <DatePicker
                inputId="license_expiration_date"
                defaultButtonLabel="Select date"
                selectButtonStyle={true}
                bind:startDate={$licenseExpirationDate.value}
                numberOfMonths={1}
                singleMode={true}
                field={licenseExpirationDate}
                on:forwardEvent={selectDatepicker}
              />
              <FormInputError form={applicationForm} errorName="license_expiration_date.required">
                License expiration date is required
              </FormInputError>
            </label>
            <label class="fizz-flex-row-8" class:fizz-stack-12={!$LayoutStore.isMobile}>
              <input
                type="checkbox"
                name="pending_liquor_license"
                class="fizz-checkbox"
                on:change={validatePendingLiquorLicenseAndSaveForm}
                bind:checked={$pendingLiquorLicense.value}
                class:fizz-input-error={!$pendingLiquorLicense.valid}
              />
              <span class="fizz-nowrap">Pending liquor license</span>
            </label>
          </fieldset>
        </fieldset>
      </div>
      {#if !$StateSettingsStore.cod_enabled}
        <div
          class="fizz-inset-vertical-24 fizz-border-bottom fizz-align-content-start"
          class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
          class:fizz-flex-row-responsive={$LayoutStore.isMobile}
        >
          <div class="fizz-flex-item">
            <h3 class="fizz-heading-3 fizz-stack-8">References</h3>
            <p class="fizz-stack-16 fizz-text-subdued">
              Provide the distributor contacts to reach out to at distributors and banks you have
              accounts with.
            </p>
          </div>
          <fieldset
            class="fizz-flex-item"
            class:fizz-full-width={$LayoutStore.isMobile}
            disabled={formDisabled || null}
          >
            <label class="fizz-full-width fizz-stack-16 fizz-inline-block">
              <p class="fizz-stack-8 fizz-label">
                <strong>Trade references</strong>
              </p>
              <textarea
                name="trade_references"
                class="fizz-full-width fizz-textarea"
                class:fizz-input-error={!$tradeReferences.valid}
                bind:value={$tradeReferences.value}
                on:blur={saveForm}
              />
              <p class="fizz-text-subdued">Distributors you currently have an account with</p>
              <FormInputError form={applicationForm} errorName="trade_references.required">
                Trade references are required
              </FormInputError>
            </label>
            <label class="fizz-full-width fizz-stack-16 fizz-inline-block">
              <p class="fizz-stack-8 fizz-label">
                <strong>Bank references</strong>
              </p>
              <textarea
                name="bank_references"
                class="fizz-full-width fizz-textarea"
                class:fizz-input-error={!$bankReferences.valid}
                bind:value={$bankReferences.value}
                on:blur={saveForm}
              />
              <p class="fizz-text-subdued">
                Name and phone number of the bank you have an account with
              </p>
              <FormInputError form={applicationForm} errorName="bank_references.required">
                Bank references are required
              </FormInputError>
            </label>
          </fieldset>
        </div>
      {/if}
      <div
        class="fizz-inset-vertical-24 fizz-align-content-start"
        class:fizz-flex-row-32-responsive={!$LayoutStore.isMobile}
        class:fizz-flex-row-responsive={$LayoutStore.isMobile}
      >
        <div class="fizz-flex-item">
          <h3 class="fizz-heading-3 fizz-stack-8">Sales rep (optional)</h3>
          <p class="fizz-stack-16 fizz-text-subdued">
            Know a sales rep already? Enter their name below so they get assigned to you.
          </p>
        </div>
        <fieldset
          class="fizz-flex-item"
          class:fizz-full-width={$LayoutStore.isMobile}
          disabled={formDisabled || null}
        >
          <label class="fizz-full-width fizz-inline-block">
            <p class="fizz-stack-8 fizz-label">
              <strong>Sales rep first and last name</strong>
            </p>
            <input
              type="text"
              name="sales_rep_name"
              class="fizz-full-width fizz-input-text"
              bind:value={$salesRepName.value}
              on:blur={saveForm}
            />
          </label>
        </fieldset>
      </div>
    </form>
  </section>
</main>
<Footer
  saveAndExit={saveAndExitForm}
  submit={submitForm}
  isDisabled={!$applicationForm.valid || formDisabled}
  exitIsDisabled={disableSaveAndExit}
  {formDisabled}
  dataTestId={"review-and-sign-button"}
  submitButtonText={"Review and sign"}
/>
